// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

import { APP_VERSION } from './version';
import { entscheide, istBeschaeftigt, merkeEingaben, zeigeKarte, kartenZustand, PRUEF_TAKT_MS, PRUEF_MINDESTABSTAND_MS } from './neueVersion';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Check if a service worker still exists.
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('[SW] Service Worker is ready (localhost)');
        });
      } else {
        // Not localhost. Register service worker.
        registerValidSW(swUrl, config);
      }
    });
  }
}

/**
 * Genau EIN Neuladen, sobald der neue Worker die Steuerung uebernimmt.
 *
 * Der frueher genutzte Weg — `SKIP_WAITING` schicken und sofort `location.reload()` —
 * ist ein Wettlauf: Der Neuladevorgang wird noch vom ALTEN Worker bedient, die Seite
 * zeigt weiter den alten Stand, und es braucht einen zweiten Ladevorgang. `controllerchange`
 * feuert dagegen erst, wenn der neue Worker wirklich uebernommen hat.
 */
let refreshing = false;
/** Die Übernahme hat diese Seite selbst ausgelöst (Knopf oder stiller Weg) — dann immer neu laden */
let selbstUebernommen = false;
/** Start oder letzte Rückkehr in die App — kurz danach darf still neu geladen werden (siehe neueVersion.ts) */
let frischSeit = Date.now();
const neuLaden = () => { if (refreshing) return; refreshing = true; window.location.reload(); };

function reloadOnControllerChange() {
  // #524: Beim allerersten Besuch gibt es noch keinen Worker. Der neue übernimmt per `clientsClaim()`
  // sofort, `controllerchange` feuert — und das Neuladen warf ~0,3 s nach dem Öffnen alles weg, was
  // schon getippt war (Anmeldung „passierte nicht“). Neu laden nur, wenn ein ALTER Worker abgelöst wird.
  // Danach gilt die Seite als gesteuert: Ein späteres Update in derselben Sitzung lädt wieder neu.
  let gesteuert = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!gesteuert) { gesteuert = true; return; }
    // Ein anderer Tab hat übernommen, hier ist aber etwas offen: nicht wegwerfen, sondern die Karte zeigen
    if (!selbstUebernommen && entscheideJetzt() === 'karte') { zeigeKarte(kartenZustand().punkte, neuLaden); return; }
    neuLaden();
  });
}

const entscheideJetzt = () => entscheide({ versteckt: document.hidden, msSeitFrisch: Date.now() - frischSeit, beschaeftigt: istBeschaeftigt() });

/** Eine wartende neue Fassung übernehmen — still oder über die Karte „Neue Version bereit“ */
function anbieten(registration: ServiceWorkerRegistration) {
  const wartend = registration.waiting;
  if (!wartend || !navigator.serviceWorker.controller) return;
  const uebernehmen = () => { selbstUebernommen = true; (registration.waiting ?? wartend).postMessage({ type: 'SKIP_WAITING' }); };
  if (entscheideJetzt() === 'still') { uebernehmen(); return; }
  frageNeuigkeiten(wartend).then(punkte => zeigeKarte(punkte, uebernehmen));
}

/** Was neu ist, weiß nur der NEUE Worker (er bringt die neue whatsNew-Liste mit) */
function frageNeuigkeiten(worker: ServiceWorker): Promise<string[]> {
  return new Promise(fertig => {
    const kanal = new MessageChannel();
    const notfalls = setTimeout(() => fertig([]), 1500);
    kanal.port1.onmessage = (e) => {
      clearTimeout(notfalls);
      fertig(Array.isArray(e.data?.punkte) ? e.data.punkte.filter((p: unknown) => typeof p === 'string') : []);
    };
    try { worker.postMessage({ type: 'NEUIGKEITEN', seit: APP_VERSION }, [kanal.port2]); } catch { clearTimeout(notfalls); fertig([]); }
  });
}

/**
 * Auch beim Zurückkehren und alle 30 Minuten nach einer neuen Fassung sehen. Ohne das prüfte die App nur beim
 * Start — und das iPhone startet eine installierte App beim Zurückkehren nicht neu.
 */
function pruefeLaufend(registration: ServiceWorkerRegistration) {
  let letzte = Date.now();
  const pruefe = () => { letzte = Date.now(); registration.update().catch(() => undefined); };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Niemand sieht hin: eine angebotene Fassung jetzt still übernehmen, wenn nichts offen ist
      if (registration.waiting && navigator.serviceWorker.controller && !istBeschaeftigt()) {
        selbstUebernommen = true;
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      return;
    }
    frischSeit = Date.now();
    if (registration.waiting) { anbieten(registration); return; }
    if (Date.now() - letzte > PRUEF_MINDESTABSTAND_MS) pruefe();
  });
  setInterval(() => { if (!document.hidden) pruefe(); }, PRUEF_TAKT_MS);
}

function registerValidSW(swUrl: string, config?: Config) {
  reloadOnControllerChange();
  merkeEingaben();
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      /**
       * Ein bereits WARTENDER Worker wurde bisher nie angestossen.
       *
       * `onupdatefound` feuert nur, wenn die Installation waehrend DIESER Sitzung
       * geschieht. Wurde sie bei einem frueheren Besuch abgeschlossen, der Uebergang
       * aber nicht vollzogen (Tab geschlossen, Neuladen verschluckt), blieb der neue
       * Worker unbegrenzt im Wartezustand — die App zeigte dauerhaft den alten Stand.
       * Genau so hing eine Installation ueber mehrere Versionen auf V5.277.0 fest.
       */
      if (registration.waiting && navigator.serviceWorker.controller) {
        console.log('[SW] Wartende Fassung gefunden.');
        anbieten(registration);
      }

      // Bei jedem Start ausdruecklich nach einer neuen Fassung sehen. Ohne das prueft der
      // Browser nur beim Registrieren und danach hoechstens alle 24 Stunden.
      registration.update().catch(() => undefined);
      pruefeLaufend(registration);

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // Neue Fassung liegt bereit — still übernehmen oder anbieten. Das Neuladen
              // erledigt `controllerchange`, nicht dieser Zweig.
              console.log('[SW] Neue Fassung bereit.');
              anbieten(registration);
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Content is cached for the first time
              console.log('[SW] Content is cached for offline use.');
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('[SW] Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[SW] No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
